import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog, DialogActions, DialogContent, Stack } from "@mui/material";

import { StatusEnum, statusLabels } from "../../shared";
import {
  CenteredLoading,
  CustomDialogTitle,
  CustomInputField,
  CustomNumberField,
  CustomSelect,
} from "../../ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./payment-term-form-constants";

export const PaymentTermFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open
      fullWidth
      component="form"
      onClose={handleClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <CustomInputField
                required
                name="name"
                label="Nama"
                register={register}
                helperText={errors.name?.message}
                error={Boolean(errors.name)}
              />
              <CustomInputField
                required
                name="description"
                label="Deskripsi"
                register={register}
                helperText={errors.description?.message}
                error={Boolean(errors.description)}
              />
              <Controller
                control={control}
                name="dueDays"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    value={field.value}
                    onChange={field.onChange}
                    inputProps={{
                      allowNegative: true,
                    }}
                    required
                    label="Jatuh Tempo (hari)"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="status"
                render={({ field, fieldState }) => (
                  <CustomSelect
                    required
                    value={field.value}
                    onChange={field.onChange}
                    id="status"
                    label="Status"
                    options={Object.values(StatusEnum).map((value) => ({
                      value: value,
                      label: statusLabels[value],
                    }))}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

PaymentTermFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
